import React, { memo, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import API from "Utils/api";
import {
  getMissionsPlansByCurrentSemanticMap,
  getIsMissionPlansLoadedForCurrentSemanticMap,
} from "Slices/missionPlans/selectors";
import { fetchDeleteMissionPlan } from "Slices/missionPlans";
import { getStopsByCurrentSemanticMapDict } from "Slices/stops";
import {
  ADD_MISSION_MODAL,
  EDIT_MISSION_MODAL,
  toggleModal,
} from "Slices/modals";
import { getCurrentSemanticMapId } from "Slices/appState";
import { useMissionPlansLoader } from "Utils/loaders";
import { MissionTimeline } from "Components/DeployedMissionList/components/MissionItem/TimelineContainer";
import { VersionSelector } from "Components/DeployedMissionList/components/MissionItem/VersionSelector";
import { MissionItemActions } from "Components/DeployedMissionList/components/MissionItemActions/MissionItemActions";
import { MissionListFooter } from "Components/DeployedMissionList/components/MissionListFooter/MissionListFooter";
import {
  sortCollectionByNumberField,
  deepSnakeCaseKeys,
} from "Utils/collectionUtils";
import { addLastDeployedToMissionPlans } from "Components/DeployedMissionList/components/MissionList/MissionList";
import ExpandDownIcon from "Components/common/assets/dropdown-iterative-down.svg";
import ExpandUpIcon from "Components/common/assets/dropdown-iterative-up.svg";

// ============================================================================
// Styles
// ============================================================================

const WrapperStyled = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
`;

const HeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
`;

const HeaderTitleStyled = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #172a3f;
`;

const CloseButtonStyled = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #606060;
  padding: 4px 8px;
  line-height: 1;

  &:hover {
    color: #172a3f;
  }
`;

const ListScrollAreaStyled = styled.div`
  overflow-y: auto;
  flex: 1;
`;

const EmptyStateStyled = styled.div`
  padding: 32px 20px;
  text-align: center;
  color: #606060;
  font-size: 14px;
`;

const MissionRowStyled = styled.div`
  border-bottom: 1px solid #e0e0e0;
`;

const MissionRowHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 8px;
`;

const ExpandButtonStyled = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const MissionInfoStyled = styled.div`
  flex: 1;
  min-width: 0;
`;

const MissionNameStyled = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #172a3f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LastDeployedStyled = styled.div`
  font-size: 12px;
  color: #606060;
  margin-top: 2px;
`;

const SelectButtonStyled = styled.button`
  height: 48px;
  padding: 16px;
  outline: 2px #3f7f7d solid;
  outline-offset: -2px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #3f7f7d;
  font-size: 18px;
  font-family: "Readex Pro", sans-serif;
  font-weight: 400;
  line-height: 24px;
  white-space: nowrap;

  &:hover {
    background: #f4f4f4;
  }
`;

const ExpandedContentStyled = styled.div`
  padding: 0 16px 12px 40px;
`;

const ExpandedFooterStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0 0;
`;

// ============================================================================
// Types
// ============================================================================

interface StationMissionListProps {
  vehicleId: string;
  onClose: () => void;
  deployPlanMissionAction: (data: {
    id: string;
    vehicle: string;
    loops: number;
    name: string;
    previous: string;
    version?: string;
    masterMissionId?: string;
  }) => void;
}

interface StationMissionRowProps {
  mission: any;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onSelect: (mission: any, versionId: string) => void;
  onDelete: (id: string) => void;
  onEdit: (mission: any) => void;
  getStopName: (stopId: string) => string;
}

// ============================================================================
// Row component — holds per-mission version selection state
// ============================================================================

const StationMissionRow = memo<StationMissionRowProps>(
  ({
    mission,
    isExpanded,
    onToggleExpand,
    onSelect,
    onDelete,
    onEdit,
    getStopName,
  }) => {
    // Mirrors MissionItem: the list row is the latest version; the selector
    // lets the operator deploy an older version by its own mission id.
    const [selectedVersionId, setSelectedVersionId] = useState<string>(
      mission.id
    );
    const [selectedVersionNumber, setSelectedVersionNumber] = useState<
      number | undefined
    >(mission.versionNumber);
    const [selectedVersionStops, setSelectedVersionStops] = useState<
      any[] | null
    >(null);
    const [loadingVersionStops, setLoadingVersionStops] = useState(false);

    const handleVersionSelect = useCallback(
      async (versionId: string, versionNumber: number) => {
        setSelectedVersionId(versionId);
        setSelectedVersionNumber(versionNumber);

        if (versionId === mission.id) {
          setSelectedVersionStops(null);
          setLoadingVersionStops(false);
          return;
        }

        // The /versions/ endpoint returns metadata only; fetch the full
        // mission so the expanded timeline reflects the chosen version.
        setLoadingVersionStops(true);
        try {
          const response = await API.get(`/missions/${versionId}/`);
          setSelectedVersionStops(response.data.stops ?? []);
        } catch (error) {
          setSelectedVersionStops(null);
        } finally {
          setLoadingVersionStops(false);
        }
      },
      [mission.id]
    );

    const sortedStops = sortCollectionByNumberField(
      selectedVersionStops ?? mission.stops ?? [],
      "order"
    );

    return (
      <MissionRowStyled>
        <MissionRowHeaderStyled>
          <ExpandButtonStyled
            onClick={() => onToggleExpand(mission.id)}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <img
              src={isExpanded ? ExpandUpIcon : ExpandDownIcon}
              alt=""
              width={20}
              height={20}
            />
          </ExpandButtonStyled>

          <MissionInfoStyled>
            <MissionNameStyled>{mission.name}</MissionNameStyled>
            <LastDeployedStyled>
              {mission.lastDeployed
                ? `Last deployed: ${mission.lastDeployed}`
                : "Never deployed"}
            </LastDeployedStyled>
          </MissionInfoStyled>

          <VersionSelector
            missionId={mission.id}
            masterMissionId={mission.masterMissionId}
            onVersionSelect={handleVersionSelect}
            selectedVersion={selectedVersionNumber}
          />

          <SelectButtonStyled
            onClick={() => onSelect(mission, selectedVersionId)}
          >
            Select
          </SelectButtonStyled>
        </MissionRowHeaderStyled>

        {isExpanded && (
          <ExpandedContentStyled>
            <MissionTimeline
              stops={sortedStops}
              getStopName={getStopName}
              loading={loadingVersionStops}
            />
            <ExpandedFooterStyled>
              <MissionItemActions
                onDelete={() => onDelete(mission.id)}
                onEdit={() => onEdit(mission)}
              />
              <SelectButtonStyled
                onClick={() => onSelect(mission, selectedVersionId)}
              >
                Select
              </SelectButtonStyled>
            </ExpandedFooterStyled>
          </ExpandedContentStyled>
        )}
      </MissionRowStyled>
    );
  }
);

StationMissionRow.displayName = "StationMissionRow";

// ============================================================================
// Component
// ============================================================================

export const StationMissionList = memo<StationMissionListProps>(
  ({ vehicleId, onClose, deployPlanMissionAction }) => {
    const dispatch = useDispatch();
    const semanticMapId = useSelector(getCurrentSemanticMapId);
    useMissionPlansLoader(semanticMapId);
    const missionPlans = useSelector(getMissionsPlansByCurrentSemanticMap);
    const isLoaded = useSelector(getIsMissionPlansLoadedForCurrentSemanticMap);
    const stopsDict = useSelector(getStopsByCurrentSemanticMapDict);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const missionPlansWithMeta = useMemo(
      () => addLastDeployedToMissionPlans(missionPlans),
      [missionPlans]
    );

    const getStopName = useCallback(
      (stopId: string) => stopsDict[stopId]?.name || "Unknown Stop",
      [stopsDict]
    );

    const handleToggleExpand = useCallback((id: string) => {
      setExpandedId((prev) => (prev === id ? null : id));
    }, []);

    const handleSelect = useCallback(
      (mission: any, versionId: string) => {
        deployPlanMissionAction({
          // Deploy the selected version's own mission id (defaults to the
          // latest version, which is the list row itself) — same contract as
          // MissionItem.deploy on the planner page.
          id: versionId || mission.id,
          name: mission.name,
          vehicle: vehicleId,
          loops: 1,
          previous: "Continue",
          version: versionId || mission.id,
          masterMissionId: mission.masterMissionId,
        });
        onClose();
      },
      [vehicleId, deployPlanMissionAction, onClose]
    );

    const handleDelete = useCallback(
      (id: string) => {
        dispatch(fetchDeleteMissionPlan(id));
      },
      [dispatch]
    );

    const handleCreateMission = useCallback(() => {
      dispatch(toggleModal({ type: ADD_MISSION_MODAL }));
    }, [dispatch]);

    const handleEdit = useCallback(
      (mission: any) => {
        const sortedStops = sortCollectionByNumberField(
          mission.stops || [],
          "order"
        );
        dispatch(
          toggleModal({
            type: EDIT_MISSION_MODAL,
            data: {
              id: mission.id,
              name: mission.name,
              timeStamp: mission.timeStamp,
              stops: deepSnakeCaseKeys(sortedStops),
              vehicle_type: mission.vehicleType ?? null,
            },
          })
        );
      },
      [dispatch]
    );

    return (
      <WrapperStyled>
        <HeaderStyled>
          <HeaderTitleStyled>Select mission</HeaderTitleStyled>
          <CloseButtonStyled onClick={onClose} aria-label="Close">
            ✕
          </CloseButtonStyled>
        </HeaderStyled>

        <ListScrollAreaStyled>
          {!isLoaded && (
            <EmptyStateStyled>Loading missions...</EmptyStateStyled>
          )}
          {isLoaded && missionPlansWithMeta.length === 0 && (
            <EmptyStateStyled>No missions available</EmptyStateStyled>
          )}
          {isLoaded &&
            missionPlansWithMeta.map((mission) => (
              <StationMissionRow
                key={mission.id}
                mission={mission}
                isExpanded={expandedId === mission.id}
                onToggleExpand={handleToggleExpand}
                onSelect={handleSelect}
                onDelete={handleDelete}
                onEdit={handleEdit}
                getStopName={getStopName}
              />
            ))}
        </ListScrollAreaStyled>

        <MissionListFooter
          onGoBack={onClose}
          onCreateMission={handleCreateMission}
        />
      </WrapperStyled>
    );
  }
);

StationMissionList.displayName = "StationMissionList";
